import { useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight, Info } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatCZK, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { SignedImg } from "@/components/shared/SignedImg";
import { ReasonChip } from "@/components/shared/DuplicateBanner";
import { toast } from "sonner";
import { ask } from "@/lib/confirm";

const KIND_LABEL = { faktura: "Přijatá faktura", uctenka: "Účtenka", vydana: "Vydaná faktura" };

// Panel „Duplicitní doklady" na Dokladech (zadání 153) — stejný vzor jako kolize v Ganttu.
// Dvojice vedle sebe, u každé „Ponechat tento" (druhý se smaže i se svými úhradami — 157/A);
// jen u podezřelých „Jsou to různé doklady" (platí, dokud se ani jeden doklad nezmění).
// 222: označená dvojice z panelu zmizí (v toastu „Vrátit"); seznam označených je
// nenápadně dole na stránce (DismissedDuplicates), ne jako druhá karta nad doklady.
export function DuplicatesPanel({ data, onOpen, onChanged }) {
  const [busy, setBusy] = useState(false);
  const pairs = data?.pairs || [];
  const open = pairs.filter((p) => !p.dismissed);
  if (!open.length) return null;

  const dismiss = async (key) => {
    setBusy(true);
    try {
      await api.post("/documents/duplicates/dismiss", { key });
      onChanged?.();
      toast.success("Označeno jako různé doklady — mezi duplicitami už nebudou.", {
        duration: 6000,
        action: { label: "Vrátit", onClick: () => api.post("/documents/duplicates/undismiss", { key }).then(() => onChanged?.()).catch(() => {}) },
      });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  // 157/A: druhý doklad se smaže i se svými úhradami. Potvrzení pojmenuje počet
  // a sumu mizejících úhrad; toast po smazání hlásí, kolik úhrad zmizelo.
  const resolve = async (keep, remove) => {
    const n = remove.payments_count || 0;
    const sum = remove.paid_total || 0;
    const msg = n > 0
      ? `Druhý doklad se smaže včetně ${n} úhrad (${formatCZK(sum)}). Pokračovat?`
      : "Druhý doklad se smaže. Pokračovat?";
    if (!await ask(msg, { ok: "Smazat druhý doklad", danger: true })) return;
    setBusy(true);
    try {
      const { data } = await api.post("/documents/duplicates/resolve", { keep: { kind: keep.kind, id: keep.id }, remove: { kind: remove.kind, id: remove.id } });
      toast.success(data.deleted_payments > 0 ? `Duplicita vyřešena · smazáno ${data.deleted_payments} úhrad` : "Duplicita vyřešena");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-[20px] border border-amber-500/40 bg-amber-500/10 p-4" data-testid="duplicates-card">
          <div className="flex items-center gap-2 mb-3 text-amber-600 dark:text-amber-400 font-bold">
            <AlertTriangle size={18} /> Duplicitní doklady — stejný doklad je v systému 2× ({data.open_count})
          </div>
          <div className="space-y-2">
            {open.map((p, i) => (
              <div key={p.key} className="rounded-[14px] bg-card border border-border p-3 text-sm" data-testid={`duplicate-pair-${i}`}>
                <div className="flex flex-wrap items-center gap-2 justify-between">
                  <div className="flex flex-wrap gap-1">{p.reasons.map((r) => <ReasonChip key={r} r={r} />)}</div>
                  <div className="text-xs text-muted-foreground">nahráno {formatDate(p.a.created_at)} a {formatDate(p.b.created_at)}</div>
                </div>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                  {[p.a, p.b].map((x, k) => {
                    const other = k === 0 ? p.b : p.a;
                    return (
                      <div key={x.id} className="min-w-0">
                        <button type="button" onClick={() => onOpen?.(x.kind, x.id)} data-testid={`duplicate-pair-${i}-doc-${k}`}
                          className="w-full text-left bg-secondary rounded-[12px] p-3 hover:bg-secondary/70 transition-colors">
                          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{KIND_LABEL[x.kind] || x.kind}{x.project_name ? ` · ${x.project_name}` : " · Režie"}</div>
                          <div className="font-bold truncate">{x.party}{x.number ? ` · č. ${x.number}` : ""}</div>
                          <div className="text-xs text-muted-foreground tabular-nums">
                            {x.date ? formatDate(x.date) : "—"} · {formatCZK(x.total)} · uhrazeno {formatCZK(x.paid_total)} · nahrál {x.created_by_name || "—"}
                          </div>
                          {x.file_url && <SignedImg url={x.file_url} alt="doklad" thumb={240} className="mt-2 h-16 w-full object-cover rounded-[8px]" />}
                        </button>
                        <Button variant="outline" disabled={busy} onClick={() => resolve(x, other)} data-testid={`duplicate-pair-${i}-keep-${k}`}
                          className="w-full h-10 mt-2 font-bold text-[13px]">Ponechat tento</Button>
                      </div>
                    );
                  })}
                </div>
                {!p.certain && (
                  <div className="mt-2 flex justify-end">
                    <button type="button" disabled={busy} onClick={() => dismiss(p.key)}
                      data-testid={`duplicate-pair-${i}-dismiss`} className="text-[13px] font-bold text-muted-foreground hover:text-foreground">Jsou to různé doklady</button>
                  </div>
                )}
              </div>
            ))}
      </div>
    </div>
  );
}

// 222: dvojice označené jako různé doklady — nenápadně dole na stránce Doklady,
// sbalené. „Vrátit mezi duplicity" je cesta zpět, kdyby se uživatel spletl.
export function DismissedDuplicates({ data, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const dismissed = (data?.pairs || []).filter((p) => p.dismissed);
  if (!dismissed.length) return null;
  const restore = async (key) => {
    setBusy(true);
    try { await api.post("/documents/duplicates/undismiss", { key }); toast.success("Dvojice je zpět mezi duplicitami."); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const side = (x) => `${x.party}${x.number ? ` · č. ${x.number}` : ""} · ${formatCZK(x.total)}`;
  return (
    <div className="min-w-0" data-testid="duplicates-dismissed-card">
      <div className="flex items-center gap-1">
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid="duplicates-dismissed-toggle"
          className="inline-flex items-center gap-1.5 min-h-[40px] text-[13px] font-semibold text-muted-foreground hover:text-foreground text-left">
          {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
          Označené jako různé doklady ({dismissed.length})
        </button>
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" aria-label="Co to znamená" className="w-8 h-8 rounded-full grid place-items-center text-muted-foreground hover:text-foreground shrink-0">
              <Info size={15} aria-hidden="true" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-72 text-sm leading-relaxed">
            Tyhle dvojice jste označili jako různé doklady, mezi duplicitami se neukazují.
            Znovu se ukážou jen tehdy, když se u některého z dokladů změní číslo, částka nebo datum.
          </PopoverContent>
        </Popover>
      </div>
      {open && (
        <div className="mt-1 rounded-[16px] border border-border bg-card divide-y divide-border">
          {dismissed.map((p, i) => (
            <div key={p.key} className="p-3 text-sm flex flex-col md:flex-row md:items-center justify-between gap-2" data-testid={`dismissed-pair-${i}`}>
              <span className="min-w-0">
                <span className="block font-semibold truncate">{side(p.a)}</span>
                <span className="block font-semibold truncate">{side(p.b)}</span>
                <span className="block text-xs text-muted-foreground">označil {p.dismissed_by || "—"} {formatDate(p.dismissed_at)}</span>
              </span>
              <button type="button" disabled={busy} onClick={() => restore(p.key)} data-testid={`dismissed-pair-${i}-undo`}
                className="self-start md:self-auto text-[13px] font-bold text-muted-foreground hover:text-foreground shrink-0 min-h-[36px]">Vrátit mezi duplicity</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
